import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { APIError, type IDSOperation } from "./api";
import { useI18n, type Translator } from "./i18n";
import {
  ManagementHeader,
  ManagementLoading,
  ManagementMessages,
  ManagementOperations,
  ManagementTabs,
  type ManagementPageProps,
} from "./managementUI";
import {
  platformCommand,
  readPlatformConfiguration,
  readPlatformOperations,
  type ImageGenerationConfiguration,
  type ImageGenerationModel,
} from "./platformServicesApi";

const PRICE_PATTERN = /^(?:0|[1-9][0-9]{0,8})(?:\.[0-9]{1,8})?$/;

function newModel(): ImageGenerationModel {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return {
    ref: `hy-image-35-${suffix}`,
    provider: "tencent-hy-image-3.5",
    display_name: "Tencent Hy Image 3.5",
    enabled: true,
    token_configured: false,
    token: "",
    clear_token: false,
    created_at_ms: Date.now(),
    prices_usd: { "1k": "0.0208", "2k": "0.0208", "4k": "0.0278" },
  };
}

function editableModel(model: ImageGenerationModel): ImageGenerationModel {
  return {
    ...model,
    token: "",
    clear_token: false,
    prices_usd: { ...model.prices_usd },
  };
}

function useHoverActionDelay(delayMilliseconds = 500) {
  const [ready, setReady] = useState(false);
  const timer = useRef<number | null>(null);
  const clear = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const onMouseEnter = useCallback(() => {
    clear();
    setReady(false);
    timer.current = window.setTimeout(() => setReady(true), delayMilliseconds);
  }, [clear, delayMilliseconds]);
  const onMouseLeave = useCallback(() => {
    clear();
    setReady(false);
  }, [clear]);
  useEffect(() => clear, [clear]);
  return { ready, onMouseEnter, onMouseLeave };
}

function ToggleSwitch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`legacy-model-toggle ${checked ? "is-on" : ""}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
    >
      <span />
    </button>
  );
}

function RowAction({
  kind,
  label,
  ready,
  disabled,
  onClick,
}: {
  kind: "edit" | "delete";
  label: string;
  ready: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`legacy-model-row-action ${kind === "delete" ? "danger" : ""} ${ready && !disabled ? "is-ready" : ""}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {kind === "edit" ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10.8 3.2 12.8 5.2 5.6 12.4H3.6V10.4L10.8 3.2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M9.4 4.6l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3.5 4.5h9M6.2 4.5V3.4c0-.5.4-.9.9-.9h1.8c.5 0 .9.4.9.9v1.1M6.4 7.2v4.1M9.6 7.2v4.1M4.8 4.5l.5 8.2c0 .6.5 1.1 1.1 1.1h3.2c.6 0 1.1-.5 1.1-1.1l.5-8.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <span>{label}</span>
    </button>
  );
}

function modelSummary(model: ImageGenerationModel) {
  return `Tencent Hy Image 3.5 · 1K $${model.prices_usd["1k"]} · 2K $${model.prices_usd["2k"]} · 4K $${model.prices_usd["4k"]}`;
}

function ModelRow({
  model,
  isDefault,
  busy,
  t,
  onDefault,
  onEdit,
  onDelete,
  onToggle,
}: {
  model: ImageGenerationModel;
  isDefault: boolean;
  busy: boolean;
  t: Translator;
  onDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const hover = useHoverActionDelay();
  const canDefault = model.enabled && model.token_configured;
  return (
    <li
      className={`legacy-model-row ${isDefault ? "is-default" : ""}`}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
      onClick={() => {
        if (!busy && !isDefault && canDefault) onDefault();
      }}
    >
      <div className="legacy-model-row-name">
        <span>{model.display_name || model.ref}</span>
        <small>{modelSummary(model)}</small>
      </div>
      {!isDefault && canDefault ? (
        <span className="legacy-model-default-hint">
          {t("点击设为默认", "Click to set as default")}
        </span>
      ) : null}
      <div className="legacy-model-row-actions" onClick={(event) => event.stopPropagation()}>
        {isDefault ? (
          <span className="legacy-model-default-badge">{t("默认", "Default")}</span>
        ) : null}
        <RowAction kind="edit" label={t("编辑", "Edit")} ready={hover.ready} disabled={busy} onClick={onEdit} />
        <RowAction kind="delete" label={t("删除", "Delete")} ready={hover.ready} disabled={busy} onClick={onDelete} />
        <ToggleSwitch
          checked={model.enabled}
          disabled={busy}
          label={t(`启用或关闭生图模型 ${model.display_name}`, `Enable or disable image model ${model.display_name}`)}
          onChange={onToggle}
        />
      </div>
    </li>
  );
}

export function ModelDialog({
  model,
  busy,
  t,
  onClose,
  onSave,
}: {
  model: ImageGenerationModel | null;
  busy: boolean;
  t: Translator;
  onClose: () => void;
  onSave: (model: ImageGenerationModel, reason: string) => Promise<void>;
}) {
  const editing = model !== null;
  const [form, setForm] = useState(() => editableModel(model ?? newModel()));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const pricesValid = (["1k", "2k", "4k"] as const).every((tier) =>
    PRICE_PATTERN.test(form.prices_usd[tier]),
  );
  const tokenReady =
    !form.enabled ||
    (!!form.token?.trim() || (form.token_configured && !form.clear_token));
  const canSubmit =
    !!form.display_name.trim() &&
    /^[A-Za-z0-9_.:-]{1,128}$/.test(form.ref.trim()) &&
    pricesValid &&
    tokenReady &&
    !!reason.trim() &&
    !busy &&
    !saving;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setFeedback("");
    try {
      await onSave(
        {
          ...form,
          ref: form.ref.trim(),
          display_name: form.display_name.trim(),
          token: form.token?.trim() ?? "",
        },
        reason.trim(),
      );
      onClose();
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : t("保存失败", "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="legacy-modal-backdrop"
      onMouseDown={(event) => {
        if (!saving && event.target === event.currentTarget) onClose();
      }}
    >
      <form className="legacy-provider-dialog" role="dialog" aria-modal="true" aria-labelledby="image-model-dialog-title" onSubmit={(event) => void submit(event)}>
        <header>
          <h2 id="image-model-dialog-title">
            {t(editing ? "编辑生图模型" : "添加生图模型", editing ? "Edit image model" : "Add image model")}
          </h2>
        </header>
        <div className="legacy-provider-dialog-body">
          <div className="legacy-model-fields">
            <div className="legacy-model-horizontal-field">
              <label>{t("服务商", "Provider")}<em>*</em></label>
              <select value={form.provider} disabled>
                <option value="tencent-hy-image-3.5">Tencent Hy Image 3.5</option>
              </select>
            </div>
            <div className="legacy-model-horizontal-field">
              <label>{t("配置名称", "Display name")}<em>*</em></label>
              <input required maxLength={128} value={form.display_name} disabled={saving} onChange={(event) => setForm({ ...form, display_name: event.target.value })} />
            </div>
            <div className="legacy-model-horizontal-field">
              <label>{t("模型标识", "Model reference")}<em>*</em></label>
              <input required maxLength={128} pattern="[A-Za-z0-9_.:-]+" value={form.ref} disabled={saving || (editing && form.token_configured)} onChange={(event) => setForm({ ...form, ref: event.target.value })} />
            </div>
            <div className="legacy-model-horizontal-field">
              <label>Token<em>*</em></label>
              <input
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                maxLength={8192}
                value={form.token ?? ""}
                required={form.enabled && !form.token_configured}
                disabled={saving || form.clear_token}
                placeholder={form.token_configured ? t("已配置；留空保留", "Configured; leave blank to keep") : t("输入 Token", "Enter token")}
                onChange={(event) => setForm({ ...form, token: event.target.value })}
              />
            </div>
            {(["1k", "2k", "4k"] as const).map((tier) => (
              <div className="legacy-model-horizontal-field" key={tier}>
                <label>{tier.toUpperCase()} {t("单张价格", "price per image")}</label>
                <div className="legacy-model-cost-control">
                  <input
                    required
                    inputMode="decimal"
                    pattern="(?:0|[1-9][0-9]{0,8})(?:\.[0-9]{1,8})?"
                    value={form.prices_usd[tier]}
                    disabled={saving}
                    onChange={(event) => setForm({ ...form, prices_usd: { ...form.prices_usd, [tier]: event.target.value } })}
                  />
                  <small>USD / {t("张", "image")}</small>
                </div>
              </div>
            ))}
            <div className="legacy-model-horizontal-field">
              <label>{t("启用模型", "Enable model")}</label>
              <div className="image-generation-switch-field">
                <ToggleSwitch
                  checked={form.enabled}
                  disabled={saving || !!form.clear_token}
                  label={t("启用此生图模型", "Enable this image model")}
                  onChange={(enabled) => setForm({ ...form, enabled })}
                />
                <span>{form.enabled ? t("已启用", "Enabled") : t("已停用", "Disabled")}</span>
              </div>
            </div>
            {editing && form.token_configured ? (
              <div className="legacy-model-horizontal-field">
                <label>{t("已保存 Token", "Saved token")}</label>
                <label className="image-generation-checkbox">
                  <input
                    type="checkbox"
                    checked={!!form.clear_token}
                    disabled={saving}
                    onChange={(event) => setForm({ ...form, clear_token: event.target.checked, token: "", ...(event.target.checked ? { enabled: false } : {}) })}
                  />
                  <span>{t("清除并停用模型", "Clear and disable model")}</span>
                </label>
              </div>
            ) : null}
            <div className="legacy-model-horizontal-field legacy-operation-reason-field">
              <label htmlFor="image-model-operation-reason">{t("操作原因", "Operation reason")}<em>*</em></label>
              <input id="image-model-operation-reason" required maxLength={512} autoComplete="off" value={reason} disabled={saving} placeholder={t("请说明本次配置原因，将写入操作审计", "Describe this configuration change; it will be recorded in the operation audit")} onChange={(event) => setReason(event.target.value)} />
            </div>
          </div>
          <p className="image-generation-dialog-help">
            {t("图片尺寸由助手按用途决定；1K、2K、4K 价格均按张以美元计费。", "The assistant chooses image dimensions. 1K, 2K, and 4K are priced per image in USD.")}
          </p>
          {feedback ? <p className="legacy-model-feedback error">{feedback}</p> : null}
        </div>
        <footer>
          <button type="button" className="legacy-secondary-button" disabled={saving} onClick={onClose}>{t("取消", "Cancel")}</button>
          <button type="submit" className="legacy-primary-button" disabled={!canSubmit}>{saving ? t("保存中…", "Saving…") : t("保存", "Save")}</button>
        </footer>
      </form>
    </div>
  );
}

function DeleteDialog({
  model,
  busy,
  t,
  onClose,
  onDelete,
}: {
  model: ImageGenerationModel;
  busy: boolean;
  t: Translator;
  onClose: () => void;
  onDelete: () => Promise<void>;
}) {
  const name = model.display_name || model.ref;
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState("");

  async function remove() {
    if (confirmation !== name || deleting || busy) return;
    setDeleting(true);
    setFeedback("");
    try {
      await onDelete();
      onClose();
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : t("删除失败", "Delete failed"));
      setDeleting(false);
    }
  }

  return (
    <div className="legacy-modal-backdrop" onMouseDown={(event) => { if (!deleting && event.target === event.currentTarget) onClose(); }}>
      <section className="legacy-delete-dialog" role="dialog" aria-modal="true">
        <header><h2>{t("删除生图模型", "Delete image model")}</h2></header>
        <div className="legacy-delete-body">
          <p>{t("此操作无法撤销。请输入生图模型名称", "This action cannot be undone. Enter the image model name")} <strong>{name}</strong> {t("以确认删除。", "to confirm deletion.")}</p>
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={t("输入生图模型名称", "Enter image model name")} disabled={deleting} />
          {feedback ? <p className="legacy-model-feedback error">{feedback}</p> : null}
        </div>
        <footer>
          <button type="button" className="legacy-secondary-button" disabled={deleting} onClick={onClose}>{t("取消", "Cancel")}</button>
          <button type="button" className="legacy-danger-button" disabled={confirmation !== name || deleting || busy} onClick={() => void remove()}>{deleting ? t("删除中…", "Deleting…") : t("删除", "Delete")}</button>
        </footer>
      </section>
    </div>
  );
}

export function ImageGenerationPage({
  session,
  refreshKey,
}: ManagementPageProps) {
  const { t } = useI18n();
  const [section, setSection] = useState<"config" | "operations">("config");
  const [config, setConfig] = useState<ImageGenerationConfiguration | null>(null);
  const [operations, setOperations] = useState<IDSOperation[]>([]);
  const [query, setQuery] = useState("");
  const [dialog, setDialog] = useState<{ type: "add" } | { type: "edit"; model: ImageGenerationModel } | null>(null);
  const [deleting, setDeleting] = useState<ImageGenerationModel | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    setNotice("");
    Promise.all([
      readPlatformConfiguration<ImageGenerationConfiguration>("image-generation", controller.signal),
      readPlatformOperations(controller.signal),
    ])
      .then(([value, history]) => {
        if (current === generation.current && !controller.signal.aborted) {
          setConfig(value);
          setOperations(history.items.filter((item) => item.target_id === "image-generation"));
        }
      })
      .catch((caught) => {
        if (!controller.signal.aborted) setError(caught);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      generation.current++;
    };
  }, [refreshKey, reload]);

  const pending = operations.some((item) => item.state === "pending");
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!config || !keyword) return config?.models ?? [];
    return config.models.filter((model) =>
      [model.display_name, model.ref, model.provider].some((value) => value.toLowerCase().includes(keyword)),
    );
  }, [config, query]);

  async function persist(next: ImageGenerationConfiguration, reason: string) {
    if (busy || pending) throw new Error(t("配置正在处理中，请稍后重试。", "Configuration is being processed. Try again shortly."));
    setBusy(true);
    setError(null);
    setNotice("");
    try {
      const saved = await platformCommand<ImageGenerationConfiguration>(
        "image-generation",
        "config",
        { expected_revision: next.revision, config: next },
        session,
        reason,
      );
      setConfig(saved);
      setNotice(t("生图模型配置已保存。", "Image model configuration saved."));
      return saved;
    } catch (caught) {
      setError(caught);
      if (!(caught instanceof APIError) || caught.status >= 500) setReload((value) => value + 1);
      throw caught;
    } finally {
      setBusy(false);
    }
  }

  async function saveDialogModel(model: ImageGenerationModel, reason: string) {
    if (!config) return;
    const editingRef = dialog?.type === "edit" ? dialog.model.ref : "";
    const models = editingRef
      ? config.models.map((item) => (item.ref === editingRef ? model : item))
      : [...config.models, model];
    await persist(
      {
        ...config,
        models,
        default_model_ref: config.default_model_ref || (model.enabled ? model.ref : ""),
      },
      reason,
    );
  }

  async function mutateModel(model: ImageGenerationModel, patch: Partial<ImageGenerationModel>, reason: string) {
    if (!config) return;
    await persist(
      {
        ...config,
        models: config.models.map((item) => item.ref === model.ref ? { ...item, ...patch } : item),
        default_model_ref: patch.enabled === false && config.default_model_ref === model.ref ? "" : config.default_model_ref,
      },
      reason,
    );
  }

  return (
    <div className="page-stack">
      <ManagementHeader
        eyebrow={t("平台服务", "Platform services")}
        title={t("生图模型", "Image generation")}
        description={t(
          "管理数据中心的 Tencent Hy Image 3.5 配置与按张美元计费。助手决定图片尺寸；对话界面不提供画质或比例选项。",
          "Manage Tencent Hy Image 3.5 configurations and per-image USD pricing. The assistant chooses dimensions; chat has no quality or aspect controls.",
        )}
      />
      <ManagementMessages error={error} notice={notice} />
      {pending ? (
        <div className="administrator-message" role="status">
          {t("操作结果待确认，请刷新后再提交。", "An operation is pending; refresh before submitting again.")}
        </div>
      ) : null}
      <button className="secondary-button" type="button" disabled={busy || loading} onClick={() => setReload((value) => value + 1)}>
        {t("刷新配置与操作记录", "Refresh configuration and operations")}
      </button>
      <ManagementTabs
        value={section}
        onChange={(value) => setSection(value)}
        items={[
          { value: "config", label: t("生图模型", "Image models"), count: config?.models.length ?? 0 },
          { value: "operations", label: t("人工操作", "Operations"), count: operations.length },
        ]}
      />
      {loading ? (
        <ManagementLoading />
      ) : section === "operations" ? (
        <ManagementOperations items={operations} />
      ) : config ? (
        <div className="legacy-provider-area">
          <section className="legacy-provider-panel">
            <div className="legacy-provider-toolbar">
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("搜索已添加的生图模型", "Search added image models")} autoComplete="off" />
              <button type="button" className="legacy-provider-add" title={t("添加生图模型", "Add image model")} aria-label={t("添加生图模型", "Add image model")} disabled={busy || pending} onClick={() => setDialog({ type: "add" })}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              </button>
            </div>
            {filtered.length === 0 ? (
              <div className="legacy-provider-empty">
                {query.trim() ? t("没有匹配的生图模型", "No matching image models") : t("尚未添加生图模型", "No image models have been added")}
              </div>
            ) : (
              <ul className="legacy-provider-list">
                {filtered.map((model) => (
                  <ModelRow
                    key={model.ref}
                    model={model}
                    isDefault={config.default_model_ref === model.ref}
                    busy={busy || pending}
                    t={t}
                    onDefault={() => void persist({ ...config, default_model_ref: model.ref }, t("设置默认生图模型", "Set default image model")).catch(() => undefined)}
                    onEdit={() => setDialog({ type: "edit", model })}
                    onDelete={() => setDeleting(model)}
                    onToggle={(enabled) => void mutateModel(model, { enabled }, t(enabled ? "启用生图模型" : "停用生图模型", enabled ? "Enable image model" : "Disable image model")).catch(() => undefined)}
                  />
                ))}
              </ul>
            )}
          </section>
          <p className="web-search-help">
            {t(
              "Token 随平台配置加密保存，不会返回团队浏览器或沙箱。若没有启用且已配置 Token 的模型，对话不会注册生图工具。",
              "Tokens are encrypted with platform configuration and never returned to team browsers or sandboxes. If no enabled model has a token, chat does not register the image generation tool.",
            )}
          </p>
        </div>
      ) : null}
      {dialog ? (
        <ModelDialog
          key={dialog.type === "edit" ? dialog.model.ref : "add"}
          model={dialog.type === "edit" ? dialog.model : null}
          busy={busy || pending}
          t={t}
          onClose={() => setDialog(null)}
          onSave={saveDialogModel}
        />
      ) : null}
      {deleting && config ? (
        <DeleteDialog
          model={deleting}
          busy={busy || pending}
          t={t}
          onClose={() => setDeleting(null)}
          onDelete={async () => {
            await persist(
              { ...config, models: config.models.filter((model) => model.ref !== deleting.ref) },
              t("删除生图模型", "Delete image model"),
            );
          }}
        />
      ) : null}
    </div>
  );
}
